import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Leaf } from "@phosphor-icons/react";

const STAGES = [[12, 0], [17, 10], [22, 20], [27, 30], [32, 40], [37, 50], [42, 60], [47, 70], [52, 80], [Infinity, 95]];
const num = (v) => { const n = parseFloat(String(v ?? "").replace(",", ".")); return Number.isFinite(n) ? n : 0; };
const landlordPct = (c) => {
  if (c.building_type === "commercial") return { pct: 50, per: null, stage: null };
  const area = num(c.heated_area_m2);
  if (!area) return { pct: null };
  const per = num(c.emissions_kg) / area;
  const i = STAGES.findIndex(([l]) => per < l);
  return { pct: STAGES[i][1], per, stage: i + 1 };
};

function Co2Block({ c, set }) {
  const r = landlordPct(c);
  return (
    <div className="space-y-2 rounded border border-emerald-200 bg-emerald-50/40 p-3" data-testid="co2-block">
      <label className="flex items-center gap-2 text-sm font-medium"><Checkbox checked={!!c.enabled} onCheckedChange={(v) => set("enabled", !!v)} data-testid="co2-enabled" /> CO2-Kosten nach CO2KostAufG aufteilen</label>
      {c.enabled && <>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">CO2-Ausstoß laut Brennstoffrechnung (kg)</Label><Input value={c.emissions_kg ?? ""} onChange={(e) => set("emissions_kg", e.target.value)} data-testid="co2-kg" /></div>
          <div><Label className="text-xs">CO2-Kosten laut Rechnung (€ brutto)</Label><Input value={c.co2_cost ?? ""} onChange={(e) => set("co2_cost", e.target.value)} data-testid="co2-cost" /></div>
          <div><Label className="text-xs">Wohnfläche des Gebäudes (m²)</Label><Input value={c.heated_area_m2 ?? ""} onChange={(e) => set("heated_area_m2", e.target.value)} data-testid="co2-area" /></div>
          <div><Label className="text-xs">Gebäudeart</Label>
            <select className="w-full h-9 border rounded px-2 text-sm bg-white" value={c.building_type || "residential"} onChange={(e) => set("building_type", e.target.value)} data-testid="co2-building-type">
              <option value="residential">Wohngebäude (Stufenmodell § 5)</option><option value="commercial">Nichtwohngebäude (50 / 50, § 8)</option></select></div>
        </div>
        <div className="text-xs text-emerald-900" data-testid="co2-result">{r.pct == null ? "Bitte Wohnfläche angeben." :
          `${r.per != null ? `${r.per.toFixed(2).replace(".", ",")} kg CO2/m²·a → Stufe ${r.stage}: ` : ""}Vermieter trägt ${r.pct} %, Mieter ${100 - r.pct} %. Der Vermieteranteil wird jedem Mieter anteilig von den Heizkosten abgezogen.`}</div>
      </>}
    </div>
  );
}

function ReductionRow({ s, r, set, allOn }) {
  return (
    <div className="border-t py-2 space-y-1.5" data-testid={`red-row-${s.tenancy_id}`}>
      <div className="text-sm font-medium">{s.tenant_name}{(s.unit_label || s.unit_number) && <span className="ml-2 text-xs font-normal text-slate-500">· {s.unit_label || s.unit_number}</span>}</div>
      <label className="flex items-center gap-2 text-xs"><Checkbox checked={!!r.hkv12 || !!allOn} disabled={!!allOn} onCheckedChange={(v) => set({ ...r, hkv12: !!v })} data-testid={`red-hkv12-${s.tenancy_id}`} /> 15 % Kürzung (§ 12 Abs. 1 HeizkostenV – nicht verbrauchsabhängig abgerechnet)</label>
      <div className="grid grid-cols-[80px_100px_1fr] gap-2">
        <Input placeholder="%" value={r.free_pct ?? ""} onChange={(e) => set({ ...r, free_pct: e.target.value })} className="h-8 text-xs" data-testid={`red-pct-${s.tenancy_id}`} />
        <Input placeholder="€" value={r.free_amount ?? ""} onChange={(e) => set({ ...r, free_amount: e.target.value })} className="h-8 text-xs" data-testid={`red-amount-${s.tenancy_id}`} />
        <Input placeholder="Begründung der freien Kürzung" value={r.free_reason ?? ""} onChange={(e) => set({ ...r, free_reason: e.target.value })} className="h-8 text-xs" data-testid={`red-reason-${s.tenancy_id}`} />
      </div>
    </div>
  );
}

export function HeatingAdjustButton({ period, property, onReload }) {
  const [open, setOpen] = useState(false);
  const [co2, setCo2] = useState({});
  const [red, setRed] = useState({});
  const [tenants, setTenants] = useState([]);
  const [all, setAll] = useState({});
  useEffect(() => {
    if (!open) return;
    setCo2({ heated_area_m2: property?.living_area || "", ...(period.co2_split || {}) });
    setRed(period.heating_reductions || {});
    setAll(period.hkv12_all || {});
    api.get(`/bka/periods/${period.id}/settlements`).then(({ data }) => setTenants((data || []).filter((s) => s.tenancy_id && !s.is_vacancy))).catch(() => setTenants([]));
  }, [open, period, property]);
  const save = async () => {
    const reds = Object.fromEntries(Object.entries(red).map(([k, r]) => [k, { ...r, free_pct: num(r.free_pct), free_amount: num(r.free_amount) }]));
    try {
      await api.put(`/bka/periods/${period.id}/heating-adjustments`, {
        co2_split: { ...co2, emissions_kg: num(co2.emissions_kg), co2_cost: num(co2.co2_cost), heated_area_m2: num(co2.heated_area_m2) }, heating_reductions: reds, hkv12_all: all });
      toast.success("Gespeichert – bitte die Abrechnung neu berechnen"); setOpen(false); onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="heating-adjust-btn"><Leaf size={14} className="mr-1" /> CO2 & Kürzungen</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="heating-adjust-dialog">
          <DialogHeader><DialogTitle>Heizkosten: CO2-Aufteilung & Kürzungen</DialogTitle>
            <DialogDescription>Die Abzüge erscheinen als eigene Zeilen in der Mieterabrechnung und mindern die Heiz-/Warmwasserkosten.</DialogDescription></DialogHeader>
          <Co2Block c={co2} set={(k, v) => setCo2((s) => ({ ...s, [k]: v }))} />
          <div className="rounded border border-amber-200 bg-amber-50/40 p-3 space-y-2" data-testid="hkv12-all-block">
            <label className="flex items-center gap-2 text-sm font-medium"><Checkbox checked={!!all.enabled} onCheckedChange={(v) => setAll((a) => ({ ...a, enabled: !!v }))} data-testid="hkv12-all-enabled" /> 15 % Kürzung für alle Mieter des Objekts (§ 12 Abs. 1 HeizkostenV)</label>
            {all.enabled && <Input placeholder="Begründung (z. B. keine Verbrauchserfassung)" value={all.reason || ""} onChange={(e) => setAll((a) => ({ ...a, reason: e.target.value }))} className="h-8 text-xs" data-testid="hkv12-all-reason" />}
          </div>
          <div className="mt-2"><div className="text-sm font-semibold">Kürzungen je Mieter</div>
            {tenants.length === 0 && <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-2 mt-1" data-testid="heating-adjust-no-settlements">Noch keine berechneten Mieterabrechnungen. Bitte Dialog schließen, „Berechnen“ klicken und den Dialog erneut öffnen, um Kürzungen je Mieter festzulegen.</p>}
            {tenants.map((s) => <ReductionRow key={s.tenancy_id} allOn={all.enabled} s={s} r={red[s.tenancy_id] || {}} set={(v) => setRed((x) => ({ ...x, [s.tenancy_id]: v }))} />)}
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)} data-testid="heating-adjust-cancel">Abbrechen</Button><Button onClick={save} data-testid="heating-adjust-save">Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
